import {
  Input,
  Label,
  MultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@simplicity/ui";

interface UserGroup {
  id: string;
  name: string;
}

/**
 * SIM-1418: one tenant-property filter group per row in the filter sidebar; selecting values
 * narrows the grid to dashboards tagged with at least one of them. SIM-1420: `school_year` and
 * `category` flow through this same shape, so the hardcoded fieldsets are gone and the sidebar is
 * fully property-driven.
 */
export interface PropertyFilterGroup {
  key: string;
  label: string;
  options: string[];
  selectedValues: string[];
}

interface FilterPanelProps {
  searchQuery: string;
  onSearch: (q: string) => void;
  userGroups?: UserGroup[];
  selectedGroupId?: string | null;
  onSelectGroup?: (groupId: string | null) => void;
  propertyFilters?: PropertyFilterGroup[];
  onTogglePropertyValue?: (propertyKey: string, value: string) => void;
}

/**
 * Diff the old selection against the new one and emit one `onToggleValue(key, value)` call per
 * changed value. Multi-checkbox UIs only change one option per click so the diff is at most a
 * single value, but the math is symmetric in case the consumer ever wires through a batch action.
 */
function emitDiff(
  propertyKey: string,
  prev: readonly string[],
  next: readonly string[],
  onToggleValue?: (propertyKey: string, value: string) => void,
) {
  if (!onToggleValue) return;
  const prevSet = new Set(prev);
  const nextSet = new Set(next);
  for (const value of next) {
    if (!prevSet.has(value)) onToggleValue(propertyKey, value);
  }
  for (const value of prev) {
    if (!nextSet.has(value)) onToggleValue(propertyKey, value);
  }
}

// SIM-1465 — property filter groups now use the shared <MultiSelect>
// primitive. The popover trigger summarizes the current selection so the
// sidebar stays compact even with 10+ options; the previous flat checkbox
// list did not scale past ~5 visible options without breaking the layout.
function PropertyFilterGroups({
  groups,
  onToggleValue,
}: {
  groups: PropertyFilterGroup[];
  onToggleValue?: (propertyKey: string, value: string) => void;
}) {
  return (
    <>
      {groups.map((group) =>
        group.options.length > 0 ? (
          <div key={group.key} className="mb-8">
            <Label
              htmlFor={`prop-${group.key}-trigger`}
              className="text-lg font-semibold block mb-3"
            >
              {group.label}
            </Label>
            <MultiSelect
              id={`prop-${group.key}-trigger`}
              value={group.selectedValues}
              options={group.options}
              placeholder="All"
              onChange={(next) => emitDiff(group.key, group.selectedValues, next, onToggleValue)}
            />
          </div>
        ) : null,
      )}
    </>
  );
}

export function FilterPanel({
  searchQuery,
  onSearch,
  userGroups = [],
  selectedGroupId,
  onSelectGroup,
  propertyFilters = [],
  onTogglePropertyValue,
}: FilterPanelProps) {
  return (
    <aside className="w-full h-full pr-8 border-r border-border" aria-label="Dashboard filters">
      <div className="sticky top-0 pt-10">
        <h2 className="text-2xl font-semibold mb-8">SimplySpark</h2>

        <div className="mb-8">
          <Label htmlFor="dashboard-search" className="text-lg font-semibold block mb-2">
            Search
          </Label>
          <Input
            id="dashboard-search"
            size="sm"
            value={searchQuery}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Filter dashboards..."
          />
        </div>

        {userGroups.length > 0 && onSelectGroup && (
          <div className="mb-8">
            <Label htmlFor="group-filter" className="text-lg font-semibold block mb-3">
              Views
            </Label>
            <Select
              value={selectedGroupId ?? "all"}
              onValueChange={(v) => onSelectGroup(v === "all" ? null : v)}
            >
              <SelectTrigger className="w-full" id="group-filter" aria-label="Filter by group">
                <SelectValue placeholder="All Groups" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Groups</SelectItem>
                {userGroups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <PropertyFilterGroups groups={propertyFilters} onToggleValue={onTogglePropertyValue} />
      </div>
    </aside>
  );
}
